import { AppLocale, defaultLocale } from '@shared/i18n';
import type { URLString } from '@shared/types';

import { env } from '../config';
import { Webview } from '../webview';
import { withoutDuplicatedSlash } from './without-duplicated-slash';

type BuildURLParams = {
  path: string;
  base?: URLString;
  locale: AppLocale;
  searchParams?: URLSearchParams;
};

type CreateURLParams = BuildURLParams & {
  isWebview?: boolean;
};

function buildURL({
  base = env.BASE_URL,
  path,
  locale,
  searchParams,
}: BuildURLParams) {
  const { origin, pathname } = new URL(base);
  const search = searchParams ? `?${searchParams.toString()}` : '';

  return locale === defaultLocale
    ? withoutDuplicatedSlash(`${origin}/${pathname}/${path}/${search}`)
    : withoutDuplicatedSlash(
        `${origin}/${locale}/${pathname}/${path}/${search}`,
      );
}

function createUrlFromPath({
  base = env.BASE_URL,
  path,
  locale,
  isWebview,
  searchParams,
}: CreateURLParams) {
  const { origin, pathname } = new URL(base);

  if (!isWebview) {
    return buildURL({ base, path, locale, searchParams });
  }

  const search = searchParams ? `?${searchParams.toString()}` : '';

  return locale === AppLocale.RU
    ? withoutDuplicatedSlash(`${origin}/${pathname}/webview/${path}${search}`)
    : withoutDuplicatedSlash(
        `${origin}/${locale}/${pathname}/webview/${path}${search}`,
      );
}

function switchLocale(locale: AppLocale) {
  const supportedLocalesRegExpString = Object.values(AppLocale).join('|');
  const localesRegex = new RegExp(`^/(${supportedLocalesRegExpString})`);
  const pathnameWithoutLocale = window.location.pathname.replace(
    localesRegex,
    '',
  );

  const localizedUrl = buildURL({
    path: pathnameWithoutLocale,
    locale,
    base: window.location.origin,
  });

  const withSearch = window.location.search
    ? `${localizedUrl}${window.location.search}`
    : localizedUrl;

  openLink({ link: withSearch });
}

interface UrlProps {
  locale: AppLocale;
  isWebview: boolean;
}

export interface ToursByIdUrlProps extends UrlProps {
  id: number;
  tourId?: string;
}

export interface HotToursV2UrlProps extends UrlProps {
  countryId?: number | string;
}

function openLink({
  link,
  target = '_self',
  isWebview = false,
}: {
  link: string;
  target?: string;
  isWebview?: boolean;
}) {
  if (isWebview) {
    Webview.openLink(link);
  } else {
    window.open(link, target, 'noopener noreferrer');
  }
}

export const NavigationLib = {
  openLink,
  buildURL,
  switchLocale,
  createUrlFromPath,
  mainPage({ locale, isWebview }: UrlProps) {
    return createUrlFromPath({ path: '/', locale, isWebview });
  },
  hotTours({ locale, isWebview }: UrlProps) {
    return createUrlFromPath({ path: '/hot-tours/', locale, isWebview });
  },
  /**
   * @param props.countryId - Preselects the country on the hot offers page
   */
  hotToursV2({ locale, isWebview, countryId }: HotToursV2UrlProps) {
    const searchParams =
      countryId === undefined || countryId === ''
        ? undefined
        : new URLSearchParams({ countryId: String(countryId) });

    return createUrlFromPath({
      path: '/tourx/hot-tours-v2/',
      locale,
      isWebview,
      searchParams,
    });
  },
  hotToursById({ locale, isWebview, id, tourId }: ToursByIdUrlProps) {
    return createUrlFromPath({
      path: `/hot-tours/${id}/${tourId}/`,
      locale,
      isWebview,
    });
  },
  toAvia(locale: AppLocale) {
    return buildURL({ base: env.AVIATA_URL, locale, path: '/' });
  },
  toRailways(locale: AppLocale) {
    return buildURL({ base: env.AVIATA_RW_URL, locale, path: '/' });
  },
  toHotels(locale: AppLocale) {
    return buildURL({ base: env.AVIATA_HOTELS_URL, locale, path: '/' });
  },
  toCabinet() {
    return buildURL({
      locale: AppLocale.RU, // Aviata cabinet does not have locale prefix api
      path: '/',
      base: env.AVIATA_CABINET_URL,
    });
  },
  toB2bLading(locale: AppLocale) {
    return buildURL({ base: env.AVIATA_B2B_URL, locale, path: '/' });
  },
  toBonusesLanding(locale: AppLocale) {
    return buildURL({
      locale,
      path: '/',
      base: env.AVIATA_LOYALTY_URL,
    });
  },
  toBlog(locale: AppLocale, utmParams: { medium: string; campaign: string }) {
    const searchParams = new URLSearchParams({
      utm_source: 'tours-aviata',
      utm_medium: utmParams.medium,
      utm_campaign: utmParams.campaign,
    });

    return buildURL({
      path: '/',
      locale,
      base: env.AVIATA_BLOG_URL,
      searchParams,
    });
  },
  toKaztourWebview(locale: AppLocale, authToken?: string) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.append('lang', locale);

    if (authToken) {
      searchParams.append('auth_token', authToken);
    }

    return buildURL({
      locale: defaultLocale,
      base: env.KAZTOUR_WEBVIEW_URL,
      path: '',
      searchParams,
    });
  },
};
